import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { buildGatePassListParams, GATE_PASS_PAGE_SIZE_OPTIONS } from "../../lib/gatePassList";
import { GATE_MODULES } from "../../lib/gateModuleConfig";
import { PageHeader, SectionCard, Pill } from "../../components/Ui";
import { Button } from "../../components/ui/button";
import { Plus, AlertCircle } from "lucide-react";
import GatePassCreateForm from "../../components/GatePassCreateForm";
import { useAuth, hasPermission } from "../../context/AuthContext";
import GatePassDetailDialog from "../../components/GatePassDetailDialog";
import AdminEntityActions from "../../components/AdminEntityActions";
import DataTableBlock from "../../components/DataTableBlock";
import DataTableExportButton from "../../components/DataTableExportButton";
import { useBootstrapList } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import { GATE_PASS_COLUMNS } from "../gatePassColumns";
import { formatIndiaDateTime } from "../../lib/indiaTime";

export default function GateModulePage({ moduleKey = "procurement" }) {
  const config = GATE_MODULES[moduleKey] || GATE_MODULES.procurement;
  const { user } = useAuth();
  const canReview = hasPermission(user, "gate.exit.approve");
  const canCreatePass = hasPermission(user, "gate.pass.create");
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [detailPassId, setDetailPassId] = useState(() => {
    const pass = searchParams.get("pass");
    return pass ? Number(pass) || pass : null;
  });

  useEffect(() => {
    const pass = searchParams.get("pass");
    if (!pass) return;
    setDetailPassId(Number(pass) || pass);
  }, [searchParams]);

  useEffect(() => {
    const current = searchParams.get("pass") || "";
    const nextId = detailPassId != null ? String(detailPassId) : "";
    if (current === nextId) return;
    const next = new URLSearchParams(searchParams);
    if (detailPassId != null) next.set("pass", String(detailPassId));
    else next.delete("pass");
    setSearchParams(next, { replace: true });
  }, [detailPassId]);

  const columns = useMemo(
    () => (config.hidePurposeColumn ? GATE_PASS_COLUMNS.filter((c) => c.id !== "purpose") : GATE_PASS_COLUMNS),
    [config.hidePurposeColumn],
  );

  const list = useBootstrapList({
    path: "/gate/bootstrap",
    columns,
    defaultSort: { id: "created_at", desc: true },
    extraParams: {
      purpose: config.purpose,
      ...(canReview ? { include_pending: 1 } : {}),
    },
    wsTypes: ["gate_pass"],
  });

  useBootstrapWS(["gate_pass"], list.reload);

  const pendingApprovals = (list.extras.pending_approvals || []).filter(
    (p) => p.purpose === config.purpose,
  );
  const inYardCount = list.meta.summary?.in_yard ?? 0;

  const exportParams = useMemo(
    () => ({
      ...buildGatePassListParams(1, list.pageSize, list.table.columnFilters, list.table.sorting),
      purpose: config.purpose,
    }),
    [config.purpose, list.pageSize, list.table.columnFilters, list.table.sorting],
  );

  const listTitle = list.table.hasActiveFilters
    ? `${list.meta.count} matching · ${inYardCount} in yard`
    : `${list.meta.count} passes · ${inYardCount} in yard`;

  return (
    <div className="space-y-6 sm:space-y-8" data-testid={config.testId}>
      <PageHeader
        eyebrow={config.eyebrow}
        title={config.title}
        description={config.description}
        actions={
          canCreatePass ? (
            <Button
              onClick={() => setOpen((v) => !v)}
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
              data-testid="new-gate-pass-button"
            >
              <Plus className="h-4 w-4 mr-1" /> {config.newPassLabel}
            </Button>
          ) : null
        }
      />

      {canReview && pendingApprovals.length > 0 && (
        <SectionCard title="Pending exit approvals">
          <p className="text-sm text-muted-foreground mb-3">
            {pendingApprovals.length} waiting for manager sign-off before check-out.
          </p>
          <div className="flex flex-wrap gap-2">
            {pendingApprovals.map((p) => (
              <Button
                key={p.id}
                type="button"
                variant="outline"
                size="sm"
                className="font-mono gap-1"
                onClick={() => setDetailPassId(p.id)}
              >
                <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                {p.code}
              </Button>
            ))}
          </div>
        </SectionCard>
      )}

      {open && (
        <SectionCard title={config.formTitle}>
          <GatePassCreateForm
            lockedPurpose={config.lockedPurpose}
            onSuccess={() => { setOpen(false); list.reload(); }}
            onCancel={() => setOpen(false)}
          />
        </SectionCard>
      )}

      <DataTableBlock
        title={listTitle}
        columns={columns}
        table={list.table}
        loading={list.loading}
        totalCount={list.meta.count}
        emptyMessage={`No ${config.title.toLowerCase()} entries yet.`}
        emptyFilteredMessage="No passes match your filters."
        onClearFilters={() => list.setPage(1)}
        testId="gate-table"
        minWidth="960px"
        renderRow={(p) => (
          <tr key={p.id} className="table-body-row" data-testid={`gate-row-${p.code}`}>
            <td className="px-3 py-2.5 font-mono text-xs">
              <button
                type="button"
                onClick={() => setDetailPassId(p.id)}
                className="link-brand font-mono text-left"
                data-testid={`gate-pass-open-${p.code}`}
              >
                {p.code}
              </button>
              {p.exit_request_status === "PENDING" && (
                <span className="ml-2 inline-block align-middle"><Pill tone="gold">Pending</Pill></span>
              )}
              {p.exit_request_status === "APPROVED" && p.status === "IN" && (
                <span className="ml-2 inline-block align-middle"><Pill tone="sage">Approved</Pill></span>
              )}
            </td>
            <td className="px-3 py-2.5">{p.vehicle_number}</td>
            <td className="px-3 py-2.5">{p.driver_name}</td>
            {!config.hidePurposeColumn && (
              <td className="px-3 py-2.5"><span className="text-xs">{p.purpose.replace("_", " ")}</span></td>
            )}
            <td className="px-3 py-2.5">{p.party_name || "—"}</td>
            <td className="px-3 py-2.5">{p.expected_variety || "—"}</td>
            <td className="px-3 py-2.5"><span className="text-xs">{formatIndiaDateTime(p.created_at) || "—"}</span></td>
            <td className="px-3 py-2.5"><span className="text-xs">{p.out_at ? formatIndiaDateTime(p.out_at) : "—"}</span></td>
            <td className="px-3 py-2.5"><Pill tone={p.status === "IN" ? "sage" : "default"}>{p.status}</Pill></td>
            <td className="px-3 py-2.5">
              {p.exit_request_status ? (
                <Pill tone={p.exit_request_status === "PENDING" ? "gold" : "sage"}>{p.exit_request_status}</Pill>
              ) : (
                "—"
              )}
            </td>
            <td className="px-3 py-2.5">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDetailPassId(p.id)}
                  className="text-xs link-brand"
                >
                  View & validate
                </button>
                <AdminEntityActions
                  entityType="gate_pass"
                  entityId={p.id}
                  entityLabel={p.code}
                  onDeleted={list.reload}
                />
              </div>
            </td>
          </tr>
        )}
        renderTrailingHeader=""
        pagination={{
          ...list.pagination,
          pageSizeOptions: GATE_PASS_PAGE_SIZE_OPTIONS,
        }}
        exportButton={(
          <DataTableExportButton
            apiPath="/gate-passes"
            params={exportParams}
            columns={columns}
            filename={`gate-${config.key}.xlsx`}
            testId="gate-export"
          />
        )}
      />

      <GatePassDetailDialog
        passId={detailPassId}
        open={!!detailPassId}
        onOpenChange={(v) => { if (!v) setDetailPassId(null); }}
        onUpdated={list.reload}
        onDeleted={list.reload}
      />
    </div>
  );
}
